import React, { useState, useEffect, useRef } from 'react';
import { Github, Linkedin, Mail, Menu, X, ChevronDown, Terminal, Server, Cpu, Globe, ArrowRight } from 'lucide-react';
import ProjectDetailModal from './components/ProjectDetailModal';
import { projectsData } from './data/projects';
import { Project } from './types/project';

// --- 3D Background Component (Monochrome Edition) ---
const ThreeBackground = () => {
  const mountRef = useRef<HTMLDivElement>(null);
  const [isThreeLoaded, setIsThreeLoaded] = useState(false);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
    script.async = true;
    script.onload = () => setIsThreeLoaded(true);
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (!isThreeLoaded || !mountRef.current || !(window as any).THREE) return;

    const THREE = (window as any).THREE;
    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0xffffff, 0.03);

    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.z = 20;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mountRef.current.appendChild(renderer.domElement);

    const geometry = new THREE.TorusKnotGeometry(10, 3, 100, 16);
    const material = new THREE.MeshBasicMaterial({
      color: 0x000000,
      wireframe: true,
      transparent: true,
      opacity: 0.08
    });
    const torusKnot = new THREE.Mesh(geometry, material);
    scene.add(torusKnot);

    const particlesGeometry = new THREE.BufferGeometry();
    const particlesCount = 800;
    const posArray = new Float32Array(particlesCount * 3);

    for(let i = 0; i < particlesCount * 3; i++) {
      posArray[i] = (Math.random() - 0.5) * 70;
    }

    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(posArray, 3));
    const particlesMaterial = new THREE.PointsMaterial({
      size: 0.04,
      color: 0x000000,
      transparent: true,
      opacity: 0.3,
    });
    const particlesMesh = new THREE.Points(particlesGeometry, particlesMaterial);
    scene.add(particlesMesh);

    let mouseX = 0;
    let mouseY = 0;

    const handleMouseMove = (event: MouseEvent) => {
      mouseX = event.clientX / window.innerWidth - 0.5;
      mouseY = event.clientY / window.innerHeight - 0.5;
    };
    document.addEventListener('mousemove', handleMouseMove);

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);

    const animate = () => {
      requestAnimationFrame(animate);
      torusKnot.rotation.x += 0.002;
      torusKnot.rotation.y += 0.004;

      particlesMesh.rotation.y = -mouseX * 0.2;
      particlesMesh.rotation.x = -mouseY * 0.2;

      camera.position.x += (mouseX * 3 - camera.position.x) * 0.05;
      camera.position.y += (-mouseY * 3 - camera.position.y) * 0.05;
      camera.lookAt(scene.position);

      renderer.render(scene, camera);
    };

    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('mousemove', handleMouseMove);
      if (mountRef.current) {
        mountRef.current.removeChild(renderer.domElement);
      }
      geometry.dispose();
      material.dispose();
      particlesGeometry.dispose();
      particlesMaterial.dispose();
    };
  }, [isThreeLoaded]);

  return <div ref={mountRef} className="fixed top-0 left-0 w-full h-full -z-10 pointer-events-none bg-white" />;
};

// --- 3D Carousel Component ---
interface ProjectCarouselProps {
  projects: Project[];
  onProjectClick: (project: Project) => void;
}

const ProjectCarousel: React.FC<ProjectCarouselProps> = ({ projects, onProjectClick }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const cardCount = projects.length;
  const displayProjects = cardCount < 6 ? [...projects, ...projects] : projects;
  const totalItems = displayProjects.length;
  const theta = 360 / totalItems;
  const radius = 350;

  const rotateCarousel = () => {
    return { transform: `translateZ(-${radius}px) rotateY(${-selectedIndex * theta}deg)` };
  };

  const handlePrev = () => setSelectedIndex(prev => prev - 1);
  const handleNext = () => setSelectedIndex(prev => prev + 1);

  return (
    <div className="relative h-[500px] w-full flex items-center justify-center overflow-hidden perspective-1000 group">
      <div
        className="absolute w-full h-full preserve-3d transition-transform duration-700 ease-out"
        style={rotateCarousel()}
      >
        {displayProjects.map((project, index) => {
          const angle = theta * index;
          return (
            <div
              key={index}
              className="absolute top-1/2 left-1/2 w-[280px] h-[360px] -ml-[140px] -mt-[180px] cursor-pointer"
              style={{
                transform: `rotateY(${angle}deg) translateZ(${radius}px)`,
                backfaceVisibility: 'visible'
              }}
              onClick={() => setSelectedIndex(index)}
            >
              <div className={`
                w-full h-full rounded-2xl p-6 flex flex-col justify-between
                transition-all duration-300 border
                ${selectedIndex === index
                  ? 'bg-black text-white border-black shadow-[0_0_30px_rgba(0,0,0,0.3)] scale-105'
                  : 'bg-white/80 text-black border-neutral-200 hover:border-neutral-500 opacity-70 hover:opacity-100'
                }
              `}>
                <div>
                  <div className="w-12 h-12 mb-4 rounded-full border border-current flex items-center justify-center">
                    {project.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{project.title}</h3>
                  <p className={`text-sm line-clamp-3 ${selectedIndex === index ? 'text-neutral-400' : 'text-neutral-600'}`}>
                    {project.shortDescription}
                  </p>
                </div>

                <div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.tags.slice(0, 3).map((tag, i) => (
                      <span key={i} className={`text-xs px-2 py-1 rounded-full border ${
                        selectedIndex === index ? 'border-white/20 bg-white/5' : 'border-black/20 bg-black/5'
                      }`}>
                        {tag}
                      </span>
                    ))}
                  </div>
                  {selectedIndex === index && (
                     <button
                       onClick={(e) => {
                         e.stopPropagation();
                         onProjectClick(project);
                       }}
                       className="flex items-center text-sm font-bold gap-2 hover:gap-3 transition-all"
                     >
                       View Project <ArrowRight className="w-4 h-4" />
                     </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="absolute inset-x-0 bottom-0 flex justify-center gap-8 p-4 z-20">
        <button onClick={handlePrev} className="p-3 rounded-full bg-black/10 hover:bg-black text-black hover:text-white transition-colors border border-black/20">
          Prev
        </button>
        <button onClick={handleNext} className="p-3 rounded-full bg-black/10 hover:bg-black text-black hover:text-white transition-colors border border-black/20">
          Next
        </button>
      </div>

      <style>{`
        .perspective-1000 { perspective: 1000px; }
        .preserve-3d { transform-style: preserve-3d; }
      `}</style>
    </div>
  );
};

// --- Main App Component ---
const App = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    budget: '',
    startDate: '',
    message: ''
  });

  const scrollToSection = (id: string) => {
    setIsMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleProjectClick = (project: Project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // 실제 구현 시 여기에 API 호출 추가
    await new Promise(resolve => setTimeout(resolve, 2000));

    alert('문의가 성공적으로 전송되었습니다! 빠른 시일 내에 연락드리겠습니다.');
    setIsSubmitting(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      projectType: '',
      budget: '',
      startDate: '',
      message: ''
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'projects', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element && element.offsetTop <= scrollPosition && (element.offsetTop + element.offsetHeight) > scrollPosition) {
          setActiveSection(section);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const techSkills = ["React", "TypeScript", "Node.js", "Python", "AWS", "Docker"];

  return (
    <div className="min-h-screen bg-white text-black font-sans selection:bg-black selection:text-white">
      <ThreeBackground />

      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex-shrink-0 font-bold text-2xl tracking-tighter text-black cursor-pointer" onClick={() => scrollToSection('home')}>
              DEV.PORTFOLIO
            </div>

            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                {['Home', 'About', 'Projects', 'Contact'].map((item) => {
                  const lowerItem = item.toLowerCase();
                  return (
                    <button
                      key={item}
                      onClick={() => scrollToSection(lowerItem)}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
                        activeSection === lowerItem ? 'text-white bg-black' : 'text-neutral-600 hover:text-black'
                      }`}
                    >
                      {item}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-neutral-600 hover:text-black hover:bg-neutral-100 focus:outline-none"
              >
                {isMenuOpen ? <X className="block h-6 w-6" /> : <Menu className="block h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden bg-white border-b border-neutral-200">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {['Home', 'About', 'Projects', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className="text-neutral-700 hover:text-black hover:bg-neutral-100 block px-3 py-2 rounded-md text-base font-medium w-full text-left"
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-center justify-center px-4 pt-16">
        <div className="text-center z-10 max-w-4xl mx-auto">
          <div className="inline-block mb-6 px-6 py-2 rounded-full border border-black/20 text-black text-sm font-semibold tracking-widest uppercase bg-white/50 backdrop-blur-sm animate-fade-in">
            Full Stack Developer
          </div>
          <h1 className="text-6xl md:text-8xl font-black text-black tracking-tighter mb-8 leading-tight">
            CODE IS <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-black via-neutral-600 to-neutral-400">
              ART & LOGIC
            </span>
          </h1>
          <p className="mt-4 max-w-2xl mx-auto text-xl text-neutral-600 mb-10 font-light">
            상상을 현실로 구현하는 미니멀리스트 개발자입니다. <br className="hidden md:block" />
            복잡함 속에서 단순함을 찾고, 최적의 사용자 경험을 설계합니다.
          </p>
          <div className="flex justify-center gap-6">
            <button
              onClick={() => scrollToSection('projects')}
              className="px-8 py-4 rounded-full bg-black text-white font-bold transition-all hover:scale-105 shadow-[0_0_20px_rgba(0,0,0,0.2)]"
            >
              View Projects
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="px-8 py-4 rounded-full bg-transparent text-black font-bold border border-black/30 hover:bg-black/10 transition-all hover:border-black"
            >
              Contact Me
            </button>
          </div>

          <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 animate-bounce">
            <ChevronDown className="w-8 h-8 text-neutral-400" />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-neutral-50 relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6 tracking-tight">About Me</h2>
            <p className="mt-4 text-neutral-600 max-w-2xl mx-auto text-lg font-light">
              기술을 통해 가치를 창출하고, 지속적으로 성장하는 개발자입니다.
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-8">
            {/* Profile Card */}
            <div className="bg-white p-8 md:p-10 rounded-2xl border border-neutral-200 shadow-sm">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
                <div className="w-32 h-32 rounded-full bg-gradient-to-br from-neutral-200 to-neutral-300 flex items-center justify-center text-4xl font-bold text-neutral-600 flex-shrink-0">
                  BSW
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-3xl font-bold text-black mb-2">변성우 (Seongwoo Byeon)</h3>
                  <p className="text-lg text-neutral-600 mb-4">Full Stack Developer</p>
                  <p className="text-neutral-700 leading-relaxed mb-6">
                    사용자 경험을 최우선으로 생각하며, 최신 기술을 활용한 혁신적인 솔루션을 개발합니다.
                    프론트엔드부터 백엔드까지 전 영역에서 가치를 창출하는 풀스택 개발자입니다.
                  </p>
                  <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                    <a
                      href="https://litt.ly/bswoo"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-5 py-2.5 bg-black text-white rounded-lg font-semibold hover:bg-neutral-800 transition-all text-sm"
                    >
                      <Globe className="w-4 h-4" />
                      <span>Littly</span>
                    </a>
                    <a
                      href="https://www.linkedin.com/in/seongwoo-byeon-81960439b"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-5 py-2.5 bg-black text-white rounded-lg font-semibold hover:bg-neutral-800 transition-all text-sm"
                    >
                      <Linkedin className="w-4 h-4" />
                      <span>LinkedIn</span>
                    </a>
                    <a
                      href="https://github.com/bsw1028"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-5 py-2.5 bg-black text-white rounded-lg font-semibold hover:bg-neutral-800 transition-all text-sm"
                    >
                      <Github className="w-4 h-4" />
                      <span>GitHub</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Education & Experience Grid */}
            <div className="grid md:grid-cols-2 gap-8">
              {/* Education */}
              <div className="bg-white p-8 rounded-2xl border border-neutral-200 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-black flex items-center justify-center">
                    <Terminal className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-black">Education</h3>
                </div>
                <div className="space-y-6">
                  <div className="border-l-2 border-neutral-200 pl-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-bold text-black">한국대학교</h4>
                      <span className="text-sm text-neutral-500">2020 - 2024</span>
                    </div>
                    <p className="text-neutral-600 text-sm mb-1">컴퓨터공학과</p>
                    <p className="text-neutral-500 text-sm">학사 학위 취득</p>
                  </div>
                  <div className="border-l-2 border-neutral-200 pl-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-bold text-black">부트캠프 / 교육과정</h4>
                      <span className="text-sm text-neutral-500">2023</span>
                    </div>
                    <p className="text-neutral-600 text-sm mb-1">풀스택 웹 개발 과정</p>
                    <p className="text-neutral-500 text-sm">6개월 집중 교육</p>
                  </div>
                </div>
              </div>

              {/* Experience */}
              <div className="bg-white p-8 rounded-2xl border border-neutral-200 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-black flex items-center justify-center">
                    <Server className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-black">Experience</h3>
                </div>
                <div className="space-y-6">
                  <div className="border-l-2 border-neutral-200 pl-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-bold text-black">테크 스타트업</h4>
                      <span className="text-sm text-neutral-500">2023 - 현재</span>
                    </div>
                    <p className="text-neutral-600 text-sm mb-1">풀스택 개발자</p>
                    <p className="text-neutral-500 text-sm">웹 서비스 개발 및 유지보수</p>
                  </div>
                  <div className="border-l-2 border-neutral-200 pl-4">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-bold text-black">프리랜서</h4>
                      <span className="text-sm text-neutral-500">2022 - 2023</span>
                    </div>
                    <p className="text-neutral-600 text-sm mb-1">웹 개발 프로젝트</p>
                    <p className="text-neutral-500 text-sm">다양한 클라이언트 프로젝트 수행</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Skills */}
            <div className="bg-white p-8 rounded-2xl border border-neutral-200 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-black flex items-center justify-center">
                  <Cpu className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-black">Tech Stack</h3>
              </div>
              <div className="flex flex-wrap gap-3">
                {techSkills.map((skill, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-neutral-50 text-neutral-700 rounded-lg border border-neutral-200 font-medium text-sm hover:border-black hover:text-black transition-colors"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            {/* Certifications & Achievements */}
            <div className="bg-white p-8 rounded-2xl border border-neutral-200 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-black flex items-center justify-center">
                  <Globe className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-black">Certifications & Awards</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 bg-black rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <h4 className="font-semibold text-black">정보처리기사</h4>
                    <p className="text-neutral-500 text-sm">한국산업인력공단, 2023</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 bg-black rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <h4 className="font-semibold text-black">AWS Certified Solutions Architect</h4>
                    <p className="text-neutral-500 text-sm">Amazon Web Services, 2023</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-2 h-2 bg-black rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <h4 className="font-semibold text-black">해커톤 우수상</h4>
                    <p className="text-neutral-500 text-sm">전국 개발자 컨퍼런스, 2022</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-32 relative overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6 tracking-tight">Selected Works</h2>
            <p className="text-neutral-500">Click to View Details</p>
          </div>

          <div className="py-10">
             <ProjectCarousel projects={projectsData} onProjectClick={handleProjectClick} />
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 bg-neutral-50 border-t border-neutral-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6 tracking-tight">프로젝트 문의</h2>
            <p className="text-neutral-600 text-lg font-light max-w-2xl mx-auto">
              개발 외주가 필요하신가요? 아래 양식을 작성해주시면 <br className="hidden md:block" />
              24시간 내에 연락드리겠습니다.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white p-6 rounded-xl border border-neutral-200 text-center">
              <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center mx-auto mb-4">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-black mb-2">이메일 문의</h3>
              <a href="mailto:contact@example.com" className="text-neutral-600 hover:text-black transition-colors text-sm">
                contact@example.com
              </a>
            </div>
            <div className="bg-white p-6 rounded-xl border border-neutral-200 text-center">
              <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center mx-auto mb-4">
                <Terminal className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-black mb-2">평균 응답 시간</h3>
              <p className="text-neutral-600 text-sm">24시간 이내</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-neutral-200 text-center">
              <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center mx-auto mb-4">
                <Globe className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-black mb-2">작업 가능 지역</h3>
              <p className="text-neutral-600 text-sm">전국 / 원격 가능</p>
            </div>
          </div>

          <div className="bg-white p-8 md:p-10 rounded-2xl border border-neutral-200 shadow-sm">
            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-black mb-2">
                    이름 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-neutral-50 border border-neutral-200 rounded-lg px-4 py-3 text-black focus:outline-none focus:border-black focus:bg-white transition-all"
                    placeholder="홍길동"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-black mb-2">
                    이메일 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-neutral-50 border border-neutral-200 rounded-lg px-4 py-3 text-black focus:outline-none focus:border-black focus:bg-white transition-all"
                    placeholder="example@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-black mb-2">
                  연락처
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full bg-neutral-50 border border-neutral-200 rounded-lg px-4 py-3 text-black focus:outline-none focus:border-black focus:bg-white transition-all"
                  placeholder="010-1234-5678"
                />
              </div>

              {/* Project Details */}
              <div className="pt-4 border-t border-neutral-200">
                <h3 className="text-lg font-bold text-black mb-4">프로젝트 정보</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-black mb-2">
                      프로젝트 유형 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-neutral-50 border border-neutral-200 rounded-lg px-4 py-3 text-black focus:outline-none focus:border-black focus:bg-white transition-all"
                    >
                      <option value="">선택해주세요</option>
                      <option value="web">웹사이트 개발</option>
                      <option value="app">모바일 앱 개발</option>
                      <option value="fullstack">풀스택 개발</option>
                      <option value="backend">백엔드 개발</option>
                      <option value="maintenance">유지보수</option>
                      <option value="consulting">컨설팅</option>
                      <option value="other">기타</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-black mb-2">
                      예산 범위 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="budget"
                      value={formData.budget}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-neutral-50 border border-neutral-200 rounded-lg px-4 py-3 text-black focus:outline-none focus:border-black focus:bg-white transition-all"
                    >
                      <option value="">선택해주세요</option>
                      <option value="under-500">500만원 미만</option>
                      <option value="500-1000">500만원 - 1,000만원</option>
                      <option value="1000-3000">1,000만원 - 3,000만원</option>
                      <option value="3000-5000">3,000만원 - 5,000만원</option>
                      <option value="over-5000">5,000만원 이상</option>
                      <option value="negotiable">협의 가능</option>
                    </select>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-black mb-2">
                    프로젝트 시작 예정일
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    className="w-full bg-neutral-50 border border-neutral-200 rounded-lg px-4 py-3 text-black focus:outline-none focus:border-black focus:bg-white transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-black mb-2">
                    프로젝트 상세 설명 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full bg-neutral-50 border border-neutral-200 rounded-lg px-4 py-3 text-black focus:outline-none focus:border-black focus:bg-white transition-all resize-none"
                    placeholder="프로젝트에 대해 자세히 설명해주세요.&#10;&#10;예시:&#10;- 프로젝트 목적과 목표&#10;- 필요한 주요 기능&#10;- 참고할 만한 사이트&#10;- 기타 요구사항"
                  ></textarea>
                </div>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full bg-black text-white font-bold py-4 rounded-lg transition-all flex items-center justify-center gap-2 ${
                    isSubmitting
                      ? 'opacity-70 cursor-not-allowed'
                      : 'hover:bg-neutral-800 hover:shadow-lg'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>전송 중...</span>
                    </>
                  ) : (
                    <>
                      <Mail className="w-5 h-5" />
                      <span>문의 보내기</span>
                    </>
                  )}
                </button>
                <p className="text-xs text-neutral-500 text-center mt-4">
                  제출하신 정보는 안전하게 보관되며, 프로젝트 검토 목적으로만 사용됩니다.
                </p>
              </div>
            </form>
          </div>

          {/* Additional Info */}
          <div className="mt-12 bg-neutral-100 p-6 rounded-xl border border-neutral-200">
            <h3 className="font-bold text-black mb-3 flex items-center gap-2">
              <Server className="w-5 h-5" />
              결제 시스템 안내
            </h3>
            <p className="text-neutral-600 text-sm leading-relaxed">
              프로젝트 계약 후, 안전한 결제를 위해 한국형 결제 시스템(토스페이먼츠, 카카오페이, 네이버페이 등)을 제공할 예정입니다.
              계약금, 중도금, 잔금으로 나누어 단계별 결제가 가능하며, 모든 거래는 투명하게 진행됩니다.
            </p>
          </div>
        </div>
      </section>

      <footer className="py-8 text-center text-neutral-400 text-xs bg-white border-t border-neutral-200">
        <p>&copy; 2024 Monochrome Portfolio. Minimalist Design.</p>
      </footer>

      {/* Project Detail Modal */}
      {selectedProject && (
        <ProjectDetailModal
          project={selectedProject}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
};

export default App;
